/**
 * Open a saved network version (edit delta) back into the Fixed Routes drawer (PRD-05c).
 *
 * Fetches the version's stored overlay (a GTFS slice of the edited/new routes), converts it
 * to editable `FixedRoute`s (reusing the GTFS→FR converter), re-stamps `networkOrigin` so the
 * routes are recognised as forks of the base feed (so re-save/re-export classify them as
 * edits again and emit real ATCO `StopPointRef`s), and merges them into the drawer — where
 * they render on the map over the base network and can be edited further.
 *
 * De-dupes against existing forks (by `networkOrigin` feed+route), so re-opening a version
 * replaces its routes rather than piling up duplicates.
 */

import { useCallback } from 'react';

import { useFixedRoutes } from '../context/FixedRoutesContext';
import { useNetworkBrowse } from '../context/NetworkBrowseContext';
import { getScenario } from '../services/network/networkApi';
import { convertGTFSFeedToFixedRoutes, type GTFSFeedData } from '../utils/fixedRoutes/gtfsConverter';
import { findExistingFork } from '../utils/fixedRoutes/networkRouteToFixedRoute';
import type { FixedRouteStop } from '../types/fixedRoutes';

const looksLikeAtco = (id: string): boolean => /^[0-9]{3}[0-9A-Za-z]{5,9}$/.test(id);

/** Recover the GTFS stop_id behind a converted stop (`gtfs-stop-{id}` or `uatStopId`). */
const gtfsStopIdOf = (s: FixedRouteStop): string =>
  s.uatStopId?.trim() || (s.id.startsWith('gtfs-stop-') ? s.id.slice('gtfs-stop-'.length) : s.id);

export interface OpenVersionResult {
  routeCount: number;
  editedCount: number;
  newCount: number;
}

export function useOpenNetworkVersion(): (scenarioId: string) => Promise<OpenVersionResult> {
  const { routes, bulkAddStopsRoutes } = useFixedRoutes();
  const { feedId, version } = useNetworkBrowse();

  return useCallback(
    async (scenarioId: string): Promise<OpenVersionResult> => {
      if (!feedId) return { routeCount: 0, editedCount: 0, newCount: 0 };
      const scenario = await getScenario(feedId, scenarioId);
      const feed = scenario.overlay?.feed as GTFSFeedData | undefined;
      if (!feed) return { routeCount: 0, editedCount: 0, newCount: 0 };

      const { routes: converted, stops } = convertGTFSFeedToFixedRoutes(feed);
      const replaced = new Set(scenario.overlay?.replacedGtfsRouteIds ?? []);
      const atcoByStopId = scenario.overlay?.atcoByStopId ?? {};

      // Re-stamp ATCO so re-export emits real StopPointRefs (BODS stop_ids are ATCO-shaped).
      const stampedStops = stops.map((s) => {
        const gid = gtfsStopIdOf(s);
        const code = atcoByStopId[gid] || (looksLikeAtco(gid) ? gid : undefined);
        return code && !s.atcoCode ? { ...s, atcoCode: code } : s;
      });

      // Re-stamp networkOrigin (fork of the base feed) + reuse an existing fork's id so
      // re-opening replaces rather than duplicates.
      const forkedAt = Date.now();
      let editedCount = 0;
      let newCount = 0;
      const stampedRoutes = converted.map((r) => {
        const gtfsRouteId = r.gtfsRouteId || r.id;
        if (replaced.has(gtfsRouteId)) editedCount += 1; else newCount += 1;
        const existing = findExistingFork(routes, feedId, gtfsRouteId);
        return {
          ...r,
          id: existing?.id ?? `route-net-${gtfsRouteId}-${forkedAt}`,
          gtfsRouteId,
          networkOrigin: { feedId, version: version ?? undefined, gtfsRouteId, forkedAt },
        };
      });

      bulkAddStopsRoutes(stampedStops, stampedRoutes, 'merge');
      return { routeCount: stampedRoutes.length, editedCount, newCount };
    },
    [feedId, version, routes, bulkAddStopsRoutes],
  );
}
